// What a routine's status pill says, and why.
//
// The whole point of the Routines redesign is that a routine states its condition on the card, so nobody
// has to open it to learn why nothing is happening. That makes this the most important pure function on
// the screen: it turns four independent booleans on the entity into ONE thing the pill can say.
//
// Precedence matters and is deliberate — a routine can be both `paused` and `pausedOutOfCredits`, and the
// credits case must win, because "you ran out of credits" is actionable and "paused" is not. Likewise a
// trigger-only routine is reported as trigger-driven rather than as "active", because "Active" next to no
// schedule reads as a bug to anyone who then goes looking for the next run.
//
// Mirrored 1:1 by `RoutineStatus.swift` and `RoutineStatus.kt`; all three unit-test the same table.

import type { BadgeVariant } from '../components/symphony/ui/Badge';

/** A routine, as far as status is concerned. */
export interface StatusableRoutine {
  paused?: boolean;
  pausedOutOfCredits?: boolean;
  /** Set by the server: a genome recalc paused this routine because its connector looks disconnected.
   *  INDEPENDENT of `paused`, like `pausedOutOfCredits`, so clearing it never disturbs a user pause. */
  pausedConnectorDisconnected?: boolean;
  /** Set by the server: the routine self-fires and is live (cron or triggers, and not paused). */
  scheduled?: boolean;
  /** Set by the server: the routine has at least one event trigger. */
  hasTriggers?: boolean;
  cron?: string;
  /** Set by the server: the genome template a connector kit built this routine from. Absent on a
   *  routine the user or the agent made — those are never an offer. */
  sourceTemplateSlug?: string;
  /** Set by the BFF: the routine has been live at least once — read off the trace pulse-identity leaves
   *  on every resume and system pause. Absent on a server older than the field, which is why the run
   *  history below is read as well. */
  activated?: boolean;
  /** Run history — a routine that has run was live, which adopts it on the rows `activated` predates.
   *  `runsKnown` gates `runCount`, exactly as it does on the card (see `routineActivity`). */
  runsKnown?: boolean;
  runCount?: number;
  /** Set by the server: the routine has ended (lib/routine-end). Its switch is locked: resuming it without a
   *  later end would only have it ended again at its next fire. */
  ended?: boolean;
}

export type RoutineStatus =
  | 'recommended'
  | 'active'
  | 'trigger'
  | 'paused'
  | 'out-of-credits'
  | 'needs-reconnect'
  | 'no-schedule';

/** The pill's Style and its catalog key, per status. */
export interface RoutineStatusBadge {
  status: RoutineStatus;
  labelKey: string;
  variant: BadgeVariant;
}

const BADGES: Record<RoutineStatus, RoutineStatusBadge> = {
  recommended: { status: 'recommended', labelKey: 'routines.status.recommended', variant: 'recommended' },
  active: { status: 'active', labelKey: 'routines.status.active', variant: 'done' },
  trigger: { status: 'trigger', labelKey: 'routines.status.trigger', variant: 'in-progress' },
  paused: { status: 'paused', labelKey: 'routines.status.paused', variant: 'paused' },
  'out-of-credits': { status: 'out-of-credits', labelKey: 'routines.status.outOfCredits', variant: 'waiting-on-you' },
  'needs-reconnect': { status: 'needs-reconnect', labelKey: 'routines.status.needsReconnect', variant: 'failed' },
  'no-schedule': { status: 'no-schedule', labelKey: 'routines.status.noSchedule', variant: 'waiting-on-a-reply' },
};

/**
 * Whether a routine is an OFFER rather than the owner's: a connector kit installed it paused, and nobody
 * has ever switched it on. This is the rule behind the Routines screen's two tabs — an offer files under
 * "Recommended for you", everything else under "Yours" — and behind the blue "Recommended" pill.
 *
 * Adoption is one-way. Turning the routine on leaves a trace pulse-identity never clears, which the BFF
 * reads back as `activated`, so pausing it again later keeps it in Yours: "I stopped it for now" and "I
 * never wanted it" are different facts, and `paused` alone says neither. The run history is the fallback
 * for a server older than the field — a routine that has run was live — read under the same `runsKnown`
 * gate the card uses, so an unseen window never adopts anything by accident.
 */
export function routineIsRecommended(routine: StatusableRoutine): boolean {
  const fromKit = typeof routine.sourceTemplateSlug === 'string' && routine.sourceTemplateSlug.trim().length > 0;
  if (!fromKit || !routine.paused) return false;
  if (routine.activated) return false;
  if (routine.runsKnown && (routine.runCount ?? 0) > 0) return false;
  return true;
}

/**
 * Resolve a routine's single status.
 *
 * Order is the contract:
 *  0. **recommended** — a kit routine the owner has never switched on (`routineIsRecommended`). Above
 *     everything, because the question the pill answers for an offer is not "why is it not running" but
 *     "this is not yours yet" — and the Recommended tab wears one pill, whatever else the flags say.
 *  1. **out of credits** — the account stopped it, and topping up is the fix. Beats plain `paused`
 *     because the entity sets both, and the generic word hides the actionable reason.
 *  2. **needs reconnect** — a genome recalc paused it because its connector looks disconnected. Above
 *     `paused` for the reason credits is: "reconnect Gmail" is actionable and "Paused" is a dead end.
 *     BELOW credits, because with no credits nothing runs at all, so reconnecting would not help and
 *     naming the connector would send the reader to fix the wrong thing. Without this case the routine
 *     fell through to `active`: a green pill and an ON toggle on something that will never fire, which
 *     is how a user's daily digest stopped with nothing on screen saying so.
 *  3. **paused** — the user stopped it. The toggle turns it back on.
 *  4. **trigger** — no cron, but at least one event trigger: it fires on an event, so there is no
 *     next-run time to show and "Active" would send the reader looking for one.
 *  5. **active** — a live cron.
 *  6. **no schedule** — neither cron nor trigger. Reachable: an LLM-authored routine whose cron never
 *     got written. It is not paused and it is not going to run, and the pill has to say so rather than
 *     letting it sit among the active ones.
 */
export function routineStatus(routine: StatusableRoutine): RoutineStatus {
  if (routineIsRecommended(routine)) return 'recommended';
  if (routine.pausedOutOfCredits) return 'out-of-credits';
  if (routine.pausedConnectorDisconnected) return 'needs-reconnect';
  if (routine.paused) return 'paused';
  const hasCron = typeof routine.cron === 'string' && routine.cron.trim().length > 0;
  if (!hasCron) return routine.hasTriggers ? 'trigger' : 'no-schedule';
  return 'active';
}

/** The pill to render for a routine. */
export function routineStatusBadge(routine: StatusableRoutine): RoutineStatusBadge {
  return BADGES[routineStatus(routine)];
}

/** Whether the on/off toggle should read as on. Only a live routine does. */
export function routineIsOn(routine: StatusableRoutine): boolean {
  return !routine.paused && !routine.pausedOutOfCredits && !routine.pausedConnectorDisconnected;
}

/**
 * Whether the user can work the toggle. A routine the *account* paused cannot be resumed from here —
 * flipping it would write `paused: false` and the next credit check would put it straight back, so the
 * control would lie. Buying credits is the way out, which the pill already names.
 *
 * A connector-paused routine is disabled for the same reason and a sharper one: `paused: false` does not
 * touch `pausedConnectorDisconnected` at all, so the toggle would not even appear to work for a moment.
 * Reconnecting the app is the way out, and the genome clears the flag on its next recalc.
 */
export function routineToggleEnabled(routine: StatusableRoutine): boolean {
  return !routine.pausedOutOfCredits && !routine.pausedConnectorDisconnected && routine.ended !== true;
}
